
import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Camera, Upload, Check, X } from 'lucide-react';
import { capturePhoto } from '../lib/api';

interface ProfilePhotoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPhotoSelected: (photoUrl: string) => void;
}

export const ProfilePhotoModal: React.FC<ProfilePhotoModalProps> = ({
  isOpen,
  onClose,
  onPhotoSelected
}) => {
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const handleCapture = async () => {
    setIsCapturing(true);
    try {
      const photoUrl = await capturePhoto();
      setCapturedPhoto(photoUrl);
    } catch (error) {
      console.error('Error capturing photo:', error);
    } finally {
      setIsCapturing(false);
    }
  };
  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsCapturing(true);
      try {
        // Pass the file to capturePhoto function
        const photoUrl = await capturePhoto(file);
        setCapturedPhoto(photoUrl);
      } catch (error) {
        console.error('Error processing uploaded file:', error);
      } finally {
        setIsCapturing(false);
      }
    }
  };
  
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };
  
  const handleConfirm = () => {
    if (capturedPhoto) {
      onPhotoSelected(capturedPhoto);
      onClose();
    }
  };
  
  const handleCancel = () => {
    setCapturedPhoto(null);
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-bauhaus-offwhite border-2 border-bauhaus-green text-bauhaus-darkgreen sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-bauhaus-darkgreen">Update Profile Photo</DialogTitle>
          <DialogDescription className="text-bauhaus-darkgreen/80">
            Take a new photo or upload one from your device.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex flex-col items-center justify-center p-2">
          {capturedPhoto ? (
            <>
              <div className="relative rounded-full overflow-hidden mb-4 w-32 h-32">
                <img 
                  src={capturedPhoto} 
                  alt="Captured" 
                  className="w-full h-full object-cover" 
                />
              </div>
              
              <div className="flex space-x-4">
                <Button variant="outline" onClick={handleCancel} className="border-bauhaus-darkgreen text-bauhaus-darkgreen hover:bg-bauhaus-darkgreen/10">
                  <X className="h-4 w-4 mr-1" />
                  Retake
                </Button>
                <Button onClick={handleConfirm} className="bg-bauhaus-green text-bauhaus-offwhite hover:bg-bauhaus-green/90">
                  <Check className="h-4 w-4 mr-1" />
                  Set as Profile
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="bg-bauhaus-cream rounded-full w-32 h-32 flex items-center justify-center mb-4 border border-bauhaus-darkgreen/30">
                {isCapturing ? (
                  <div className="animate-pulse">
                    <Camera className="h-12 w-12 text-bauhaus-darkgreen/70" />
                  </div>
                ) : (
                  <Camera className="h-12 w-12 text-bauhaus-darkgreen/70" />
                )}
              </div>
              
              <div className="flex space-x-4">
                <Button onClick={handleCapture} disabled={isCapturing} className="bg-bauhaus-green text-bauhaus-offwhite hover:bg-bauhaus-green/90">
                  <Camera className="h-4 w-4 mr-1" />
                  Take Photo
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleUploadClick} 
                  disabled={isCapturing} 
                  className="border-bauhaus-darkgreen text-bauhaus-darkgreen hover:bg-bauhaus-darkgreen/10"
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Upload
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
